/**
 * ============================================================================
 * [P3-03] Firestore Scripts Service
 *
 * 스크립트(scripts) 컬렉션에 대한 CRUD 및 실시간 구독 함수
 *
 * 기능:
 * - createScript: 스크립트 생성
 * - updateScript: 스크립트 수정
 * - deleteScript: 스크립트 삭제
 * - getScripts: 스크립트 목록 조회
 * - subscribeToScripts: 실시간 구독
 *
 * Safety:
 * - withRetry()로 네트워크 에러 대응
 * - 모든 함수에서 userId/scriptId 검증
 * - onSnapshot unsubscribe 반환으로 메모리 누수 방지
 *
 * [2026-01-28] P3-03 Firestore 연동 구현
 * ============================================================================
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  getDocs,
  getDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  type CollectionReference,
  type Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { withRetry } from "@/utils/common";
import type { Script } from "@/types";

// ============================================================================
// Firestore 데이터 타입
// ============================================================================
interface FirestoreScriptData {
  name: string;
  content: string;
  variables: string[];
  category?: string;
  llmModel?: string;
  llmModelType?: string;
  completedAt?: Timestamp | string; // 레거시 string 저장분 읽기 호환
  seriesId?: string;
  episode?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// 컬렉션 참조 헬퍼
// ============================================================================
const getScriptsCollection = (userId: string): CollectionReference =>
  collection(db, "users", userId, "scripts");

// ============================================================================
// 변수 추출 헬퍼 ({{변수명}} 패턴)
// ============================================================================
const VARIABLE_REGEX = /\{\{(\w+)\}\}/g;

function extractVariables(content: string): string[] {
  const matches = content.matchAll(VARIABLE_REGEX);
  const vars = new Set<string>();
  for (const match of matches) {
    vars.add(match[1]);
  }
  return Array.from(vars);
}

// ============================================================================
// completedAt 쓰기 정규화 (string → Date → Timestamp 저장)
// - Invalid Date는 undefined 반환 = 필드 생략 (무효값 저장 금지)
// ============================================================================
function normalizeCompletedAt(value: Date | string): Date | undefined {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

// ============================================================================
// Firestore → Script 변환
// ============================================================================
// ============================================================================
// Firestore → Script 변환
// ============================================================================
function scriptFromFirestore(docSnap: { id: string; data: () => any }): Script {
  const data = docSnap.data() as FirestoreScriptData | undefined;
  if (!data) {
    throw new Error("[scriptFromFirestore] Document data is undefined");
  }

  return {
    id: docSnap.id,
    name: data.name || "",
    content: data.content || "",
    variables: data.variables || [],
    category: data.category || undefined,
    llmModel: data.llmModel || undefined,
    llmModelType: data.llmModelType || undefined,
    completedAt:
      typeof data.completedAt === "string"
        ? data.completedAt // 레거시 string 저장분은 그대로 보존
        : data.completedAt?.toDate?.() ?? undefined,
    seriesId:
      typeof data.seriesId === "string" ? data.seriesId || undefined : undefined,
    episode:
      typeof data.episode === "number" && Number.isFinite(data.episode)
        ? data.episode
        : undefined,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
  };
}

// ============================================================================
// [P3-03-A] 스크립트 생성 (Create)
// ============================================================================
export async function createScript(
  userId: string,
  scriptData: Omit<Script, "id" | "createdAt" | "updatedAt">,
): Promise<Script> {
  // 1. Validation
  if (!userId) {
    throw new Error("[createScript] userId is required");
  }
  if (!scriptData.name?.trim()) {
    throw new Error("[createScript] name is required");
  }

  // 2. Firestore에 추가 (재시도 로직 포함)
  return withRetry(
    async () => {
      const scriptsRef = getScriptsCollection(userId);

      // 변수 자동 추출
      const variables = extractVariables(scriptData.content || "");

      const docData: Record<string, unknown> = {
        name: scriptData.name.trim(),
        content: scriptData.content || "",
        variables,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // 선택 필드 추가 (undefined 제외)
      if (scriptData.category?.trim()) {
        docData.category = scriptData.category.trim();
      }
      if (scriptData.llmModel?.trim()) {
        docData.llmModel = scriptData.llmModel.trim();
      }
      if (scriptData.llmModelType?.trim()) {
        docData.llmModelType = scriptData.llmModelType.trim();
      }
      if (scriptData.completedAt) {
        const completedAt = normalizeCompletedAt(scriptData.completedAt);
        if (completedAt) {
          docData.completedAt = completedAt;
        }
      }
      if (scriptData.seriesId?.trim()) {
        docData.seriesId = scriptData.seriesId.trim();
      }
      if (
        typeof scriptData.episode === "number" &&
        Number.isFinite(scriptData.episode)
      ) {
        docData.episode = scriptData.episode;
      }

      // 문서 추가
      const docRef = await addDoc(scriptsRef, docData);

      // 생성된 문서 조회
      const newDoc = await getDoc(docRef);
      if (!newDoc.exists()) {
        throw new Error("[createScript] Failed to create document");
      }

      console.log("[createScript] 스크립트 생성 완료:", docRef.id);
      return scriptFromFirestore(newDoc);
    },
    3,
    1000,
  ); // maxRetries=3, baseDelay=1000ms
}

// ============================================================================
// [P3-03-B] 스크립트 수정 (Update)
// ============================================================================
export async function updateScript(
  userId: string,
  scriptId: string,
  updates: Partial<Script>,
): Promise<void> {
  // 1. Validation
  if (!userId) {
    throw new Error("[updateScript] userId is required");
  }
  if (!scriptId) {
    throw new Error("[updateScript] scriptId is required");
  }

  // 빈 업데이트는 무시
  const updateKeys = Object.keys(updates).filter(
    (key) => key !== "id" && key !== "createdAt",
  );
  if (updateKeys.length === 0) {
    console.warn("[updateScript] No valid updates provided, skipping");
    return;
  }

  // 2. Firestore 업데이트 (재시도 로직 포함)
  return withRetry(
    async () => {
      const scriptRef = doc(db, "users", userId, "scripts", scriptId);

      // 업데이트 데이터 준비
      const safeUpdates: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };

      if (updates.name !== undefined) {
        safeUpdates.name = updates.name.trim();
      }
      if (updates.content !== undefined) {
        safeUpdates.content = updates.content;
        // 콘텐츠 변경 시 변수 재추출
        safeUpdates.variables = extractVariables(updates.content);
      }
      if (updates.category !== undefined) {
        safeUpdates.category = updates.category?.trim() || "";
      }
      if (updates.llmModel !== undefined) {
        safeUpdates.llmModel = updates.llmModel?.trim() || "";
      }
      if (updates.llmModelType !== undefined) {
        safeUpdates.llmModelType = updates.llmModelType?.trim() || "";
      }
      // 빈 값 = deleteField()로 필드 자체를 제거 (부재=미지정, 센티널 저장 금지)
      // completedAt은 'in' 존재 검사: 명시적 undefined 전달(완성 해제)과 키 생략을 구분
      if ("completedAt" in updates) {
        if (updates.completedAt === undefined) {
          safeUpdates.completedAt = deleteField();
        } else {
          const completedAt = normalizeCompletedAt(updates.completedAt);
          if (completedAt) {
            safeUpdates.completedAt = completedAt;
          }
        }
      }
      // seriesId/episode도 'in' 존재 검사: 명시적 undefined 전달(해제)과 키 생략을 구분
      if ("seriesId" in updates) {
        const seriesId =
          updates.seriesId === undefined ? "" : updates.seriesId.trim();
        safeUpdates.seriesId = seriesId || deleteField();
      }
      if ("episode" in updates) {
        safeUpdates.episode = Number.isFinite(updates.episode)
          ? updates.episode
          : deleteField();
      }

      await updateDoc(scriptRef, safeUpdates);
      console.log("[updateScript] 스크립트 수정 완료:", scriptId);
    },
    3,
    1000,
  );
}

// ============================================================================
// [P3-03-C] 스크립트 삭제 (Delete)
// ============================================================================
export async function deleteScript(
  userId: string,
  scriptId: string,
): Promise<void> {
  // 1. Validation
  if (!userId) {
    throw new Error("[deleteScript] userId is required");
  }
  if (!scriptId) {
    throw new Error("[deleteScript] scriptId is required");
  }

  // 2. Firestore 삭제 (재시도 로직 포함)
  return withRetry(
    async () => {
      const scriptRef = doc(db, "users", userId, "scripts", scriptId);
      await deleteDoc(scriptRef);
      console.log("[deleteScript] 스크립트 삭제 완료:", scriptId);
    },
    3,
    1000,
  );
}

// ============================================================================
// [P3-03-D] 스크립트 목록 조회
// ============================================================================
export async function getScripts(userId: string): Promise<Script[]> {
  // 1. Validation
  if (!userId) {
    throw new Error("[getScripts] userId is required");
  }

  // 2. Firestore 조회 (재시도 로직 포함)
  return withRetry(
    async () => {
      const scriptsRef = getScriptsCollection(userId);
      const q = query(scriptsRef, orderBy("updatedAt", "desc"));
      const snapshot = await getDocs(q);

      const scripts = snapshot.docs.map((docSnap) =>
        scriptFromFirestore(docSnap),
      );
      console.log("[getScripts] 조회 완료:", scripts.length, "개");

      return scripts;
    },
    3,
    1000,
  );
}

// ============================================================================
// [P3-03-E] 실시간 구독 (Subscribe)
// - CRITICAL: 반환된 unsubscribe 함수를 반드시 cleanup에서 호출할 것
// ============================================================================
export function subscribeToScripts(
  userId: string,
  onData: (scripts: Script[]) => void,
  onError?: (error: Error) => void,
): () => void {
  // 1. Validation - userId가 없으면 no-op 반환
  if (!userId) {
    console.warn(
      "[subscribeToScripts] userId is null, returning no-op unsubscribe",
    );
    return () => {
      // no-op
    };
  }

  // 2. 쿼리 빌드
  const scriptsRef = getScriptsCollection(userId);
  const q = query(scriptsRef, orderBy("updatedAt", "desc"));

  // 3. 구독 시작
  console.log("[subscribeToScripts] 실시간 구독 시작:", userId);

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const scripts = snapshot.docs.map((docSnap) =>
        scriptFromFirestore(docSnap),
      );
      console.log("[subscribeToScripts] 데이터 수신:", scripts.length, "개");
      onData(scripts);
    },
    (error) => {
      console.error("[subscribeToScripts] 구독 에러:", error);
      onError?.(error);
    },
  );

  // 4. CRITICAL: unsubscribe 함수 반환 (메모리 누수 방지)
  return () => {
    console.log("[subscribeToScripts] 구독 해제:", userId);
    unsubscribe();
  };
}
